'use client';
import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@core/ui/primitives/collapsible';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/core/ui/primitives/sidebar';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { Skeleton } from '@core/ui/primitives/skeleton';
import { Badge } from '@core/ui/primitives/badge';
import { LuChevronRight, LuPlus, LuTrash2 } from 'react-icons/lu';
import type { OrganizationNavItem, OrganizationSidebarSelection } from './OrganizationSidebar';
export const CREATE_VAULT_LABEL = 'Create vault';
const LABEL_TRANSITION =
  'transition-[opacity,transform] duration-200 ease-sidebar-smooth group-data-[collapsed=true]/sidebar-wrapper:opacity-0 group-data-[collapsed=true]/sidebar-wrapper:translate-x-3 group-data-[collapsed=true]/sidebar-wrapper:pointer-events-none';
export interface OrganizationSidebarNavProps {
  items: OrganizationNavItem[];
  onSelect?: (selection: OrganizationSidebarSelection) => void;
  selectedKey: string | null;
  loading?: boolean;
  error?: string | null;
  activeOrganizationId?: string | null;
  activeOrganizationSlug?: string | null;
}
export function OrganizationSidebarNav({
  items,
  onSelect,
  selectedKey,
  loading,
  error,
  activeOrganizationId,
  activeOrganizationSlug,
}: OrganizationSidebarNavProps) {
  // Vaults section is open by default
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ Vaults: true });
  // Track if user has interacted - only animate after first interaction
  const [hasInteracted, setHasInteracted] = useState(false);

  const createSelectionKey = `Vaults|${CREATE_VAULT_LABEL}`;
  return (
    <SidebarGroup className='flex-1'>
      {error && !loading && <div className='px-4 pb-2 text-xs text-red-500'>{error}</div>}
      <SidebarMenu>
        {items.map((item) => {
          // Always render the "Vaults" section as a collapsible group so the create control
          // is visible even when there are no vaults yet.
          const hasChildren = item.title === 'Vaults' || Boolean(item.items?.length);
          if (!hasChildren) {
            const key = `${item.title}|${item.title}`;
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  className='group/sidebar-link overflow-hidden justify-start text-left transition-all duration-300 ease-sidebar-smooth group-data-[collapsed=true]/sidebar-wrapper:justify-center group-data-[collapsed=true]/sidebar-wrapper:px-0 data-[active=true]:bg-muted-foreground/15 data-[active=true]:text-foreground data-[active=true]:font-semibold focus-visible:ring-0'
                  tooltip={item.title}
                  type='button'
                  isActive={selectedKey === key}
                  onClick={() => onSelect?.({ section: item.title, label: item.title })}>
                  {item.icon && (
                    <item.icon className='h-4 w-4 transition-transform duration-300 ease-sidebar-smooth group-data-[collapsed=true]/sidebar-wrapper:scale-90' />
                  )}
                  <span className={cn('flex-1 truncate text-sm font-medium', LABEL_TRANSITION)}>{item.title}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          }
          const sectionKey = `${item.title}|`;
          const hasVaultItems = (item.items?.length ?? 0) > 0;
          const isOpen = openSections[item.title] ?? false;
          return (
            <Collapsible
              key={item.title}
              asChild
              open={isOpen}
              onOpenChange={(nextOpen) => {
                setHasInteracted(true);
                setOpenSections((prev) => ({ ...prev, [item.title]: nextOpen }));
              }}
              className='group/collapsible'>
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    className='group/sidebar-link overflow-hidden justify-start text-left transition-all duration-300 ease-sidebar-smooth hover:bg-transparent hover:text-sidebar-foreground focus:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:outline-none active:bg-transparent active:text-sidebar-foreground group-data-[collapsed=true]/sidebar-wrapper:justify-center group-data-[collapsed=true]/sidebar-wrapper:px-0'
                    tooltip={item.title}
                    type='button'
                    isActive={selectedKey?.startsWith(sectionKey) ?? false}>
                    {item.icon && (
                      <item.icon className='h-4 w-4 transition-transform duration-300 ease-sidebar-smooth group-data-[collapsed=true]/sidebar-wrapper:scale-90' />
                    )}
                    <span className={cn('flex-1 truncate text-sm font-medium', LABEL_TRANSITION)}>{item.title}</span>
                    {item.title === 'Vaults' && (
                      <span
                        className={cn(
                          'mr-1 flex h-5 w-5 items-center justify-center rounded-md text-muted-foreground transition-colors',
                          'hover:bg-primary/10 hover:text-primary',
                          'group-data-[collapsed=true]/sidebar-wrapper:hidden',
                          selectedKey === createSelectionKey && 'text-primary'
                        )}
                        aria-label='Add vault'
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setOpenSections((prev) => ({ ...prev, [item.title]: true }));
                          onSelect?.({
                            section: item.title,
                            label: CREATE_VAULT_LABEL,
                            organizationId: activeOrganizationId ?? null,
                            vaultId: null,
                            vaultSlug: null,
                          });
                        }}>
                        <LuPlus className='h-4 w-4' />
                      </span>
                    )}
                    <span
                      className={cn(
                        'ml-auto flex h-5 w-5 items-center justify-center rounded-md text-muted-foreground transition-colors',
                        'hover:bg-primary/10 hover:text-primary',
                        'group-data-[collapsed=true]/sidebar-wrapper:opacity-0 group-data-[collapsed=true]/sidebar-wrapper:-translate-x-2'
                      )}>
                      <LuChevronRight className='h-4 w-4 shrink-0 transition-transform duration-200 ease-sidebar-smooth group-data-[state=open]/collapsible:rotate-90' />
                    </span>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent
                  className={cn(
                    'overflow-hidden',
                    hasInteracted &&
                      'data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down'
                  )}>
                  <SidebarMenuSub className={cn(
                    'mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-separator px-2.5 py-0.5 group-data-[collapsed=true]/sidebar-wrapper:hidden',
                    // Reduce opacity when refreshing existing items
                    loading && hasVaultItems && 'opacity-50 pointer-events-none'
                  )}>
                    {hasVaultItems ? (
                      item.items?.map((subItem) => {
                        const subKey = subItem.vaultId
                          ? `${item.title}|${subItem.vaultId}`
                          : `${item.title}|${subItem.title}`;
                        const isDeleted = subItem.isDeleted ?? false;
                        return (
                          <SidebarMenuSubItem key={subItem.title}>
                            <SidebarMenuSubButton
                              type='button'
                              className={cn(
                                'flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-left transition-all duration-300 ease-sidebar-smooth data-[active=true]:bg-muted-foreground/15 data-[active=true]:text-foreground data-[active=true]:font-semibold focus-visible:ring-0',
                                isDeleted && 'opacity-60'
                              )}
                              isActive={selectedKey === subKey}
                              onClick={() =>
                                onSelect?.({
                                  section: item.title,
                                  label: subItem.title,
                                  vaultId: subItem.vaultId ?? null,
                                  vaultSlug: subItem.vaultSlug ?? null,
                                  organizationId: subItem.organizationId ?? null,
                                  isDeleted,
                                })
                              }>
                              {isDeleted && (
                                <LuTrash2 className='h-3 w-3 shrink-0 text-muted-foreground' />
                              )}
                              <span className={cn(LABEL_TRANSITION, 'truncate')}>{subItem.title}</span>
                              {isDeleted && (
                                <Badge variant='secondary' className='ml-auto shrink-0 text-[10px] px-1.5 py-0 h-4'>
                                  Trash
                                </Badge>
                              )}
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        );
                      })
                    ) : loading ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <SidebarMenuSubItem key={i}>
                          <div className='flex h-7 min-w-0 -translate-x-px items-center px-2'>
                            <Skeleton className='h-4 w-24 rounded-md bg-sidebar-accent' />
                          </div>
                        </SidebarMenuSubItem>
                      ))
                    ) : (
                      <SidebarMenuSubItem>
                        <div className='flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-xs text-muted-foreground'>
                          <span className={LABEL_TRANSITION}>
                            Create your first vault for this organization using the + above.
                          </span>
                        </div>
                      </SidebarMenuSubItem>
                    )}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
